'use client';

/**
 * Focus targets for workspace artifact opens ("open the spec at page 3 and
 * highlight this box", "select A120:I120 in the BOM").
 *
 * Artifact opens travel through several panel hosts (org chat, Tech Studio,
 * in-place pages) that only carry a path. Rather than thread a focus through
 * each host's panel state, an open records it here and the viewer for that
 * path claims it: on mount for a newly opened file, or through the
 * subscription when the file is already showing. The open itself still goes
 * through the normal artifact path, so the viewer loads the file with its
 * usual read checks; this store holds only a path and where to look.
 */

/** A box normalized 0–1 against the page or image, origin top-left. */
export type WorkspaceArtifactRegion = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type WorkspaceArtifactFocus = {
  /** 1-based page a paged viewer (PDF) opens at. */
  page?: number;
  /** Area to highlight on that page or image. */
  region?: WorkspaceArtifactRegion;
  /** A1 range to select in a spreadsheet, optionally `Sheet!A1:B2`. */
  range?: string;
  /** Question id to highlight in a `.questions` answer file. */
  question?: string;
  /**
   * The open cites this file (a sheet's Source): with no `region`, the whole
   * cited page or image is marked so the reader still sees what was meant.
   */
  cited?: boolean;
  /**
   * Where the citing cell sat on screen (viewport px). Viewers that can pan
   * or scroll land the cited area there, so the eye does not have to move.
   */
  origin?: WorkspaceArtifactScreenRect;
  /** Top-left cell to scroll a spreadsheet back to (0-based). */
  viewport?: WorkspaceArtifactSheetViewport;
  /**
   * The citing cell's text and its column header, so a spreadsheet source
   * can point at the one cell of a cited row the value came from.
   */
  cellValue?: string;
  cellHeader?: string;
  /** The citation's own words (its evidence label), a second hint. */
  cellLabel?: string;
};

/** A box on screen in CSS pixels (viewport coordinates). */
export type WorkspaceArtifactScreenRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * A spreadsheet's scroll: the top-left cell (0-based) plus the pixels the
 * grid is scrolled past that cell's corner.
 */
export type WorkspaceArtifactSheetViewport = {
  row: number;
  column: number;
  offsetX?: number;
  offsetY?: number;
};

type FocusRequestListener = (
  path: string,
  focus: WorkspaceArtifactFocus
) => void;

/**
 * A request outlives a viewer that mounts and unmounts before it can show
 * it (the panel re-renders a tab while the file resolves), but not this
 * long: an unclaimed request must not resurface on a later, plain open.
 */
const REQUEST_TTL_MS = 30_000;

const pendingFocus = new Map<
  string,
  { focus: WorkspaceArtifactFocus; at: number }
>();
const listeners = new Set<FocusRequestListener>();

function requestKey(path: string): string {
  return path.trim();
}

export function normalizeWorkspaceArtifactPage(
  page: number | null | undefined
): number | undefined {
  return typeof page === 'number' && Number.isInteger(page) && page >= 1
    ? page
    : undefined;
}

export function normalizeWorkspaceArtifactRegion(
  region: unknown
): WorkspaceArtifactRegion | undefined {
  if (typeof region !== 'object' || region === null) return undefined;
  const { x, y, width, height } = region as Record<string, unknown>;
  const values = [x, y, width, height];
  if (!values.every((n) => typeof n === 'number' && Number.isFinite(n))) {
    return undefined;
  }
  const [left, top, w, h] = values as number[];
  if (left! < 0 || top! < 0 || w! <= 0 || h! <= 0) return undefined;
  if (left! + w! > 1.000001 || top! + h! > 1.000001) return undefined;
  return { x: left!, y: top!, width: w!, height: h! };
}

function normalizeScreenRect(
  rect: unknown
): WorkspaceArtifactScreenRect | undefined {
  if (typeof rect !== 'object' || rect === null) return undefined;
  const { x, y, width, height } = rect as Record<string, unknown>;
  const values = [x, y, width, height];
  if (!values.every((n) => typeof n === 'number' && Number.isFinite(n))) {
    return undefined;
  }
  const [left, top, w, h] = values as number[];
  if (w! < 0 || h! < 0) return undefined;
  return { x: left!, y: top!, width: w!, height: h! };
}

function normalizeSheetViewport(
  viewport: unknown
): WorkspaceArtifactSheetViewport | undefined {
  if (typeof viewport !== 'object' || viewport === null) return undefined;
  const { row, column, offsetX, offsetY } = viewport as Record<string, unknown>;
  const index = (n: unknown) =>
    typeof n === 'number' && Number.isInteger(n) && n >= 0;
  const pixels = (n: unknown) =>
    typeof n === 'number' && Number.isFinite(n) && n >= 0;
  if (!index(row) || !index(column)) return undefined;
  return {
    row: row as number,
    column: column as number,
    ...(pixels(offsetX) && offsetX !== 0 ? { offsetX: offsetX as number } : {}),
    ...(pixels(offsetY) && offsetY !== 0 ? { offsetY: offsetY as number } : {}),
  };
}

/** Keep only well-formed fields; an empty focus still clears a highlight. */
export function normalizeWorkspaceArtifactFocus(
  focus: WorkspaceArtifactFocus
): WorkspaceArtifactFocus {
  const page = normalizeWorkspaceArtifactPage(focus.page);
  const region = normalizeWorkspaceArtifactRegion(focus.region);
  const range = focus.range?.trim();
  const question = focus.question?.trim();
  const origin = normalizeScreenRect(focus.origin);
  const viewport = normalizeSheetViewport(focus.viewport);
  const text = (value: unknown) =>
    typeof value === 'string' ? value.trim().slice(0, 500) : '';
  const cellValue = text(focus.cellValue);
  const cellHeader = text(focus.cellHeader);
  const cellLabel = text(focus.cellLabel);
  return {
    ...(page ? { page } : {}),
    ...(region ? { region } : {}),
    ...(range ? { range } : {}),
    ...(question ? { question } : {}),
    ...(focus.cited === true ? { cited: true } : {}),
    ...(origin ? { origin } : {}),
    ...(viewport ? { viewport } : {}),
    ...(cellValue ? { cellValue } : {}),
    ...(cellHeader ? { cellHeader } : {}),
    ...(cellLabel ? { cellLabel } : {}),
  };
}

/** Ask the viewer showing `path` to focus there; a newer request replaces it. */
export function requestWorkspaceArtifactFocus(
  path: string,
  focus: WorkspaceArtifactFocus
): void {
  const key = requestKey(path);
  if (!key) return;
  const target = normalizeWorkspaceArtifactFocus(focus);
  pendingFocus.set(key, { focus: target, at: Date.now() });
  for (const listener of listeners) listener(key, target);
}

/** The focus still waiting for `path`, without claiming it. */
export function peekWorkspaceArtifactFocusRequest(
  path: string
): WorkspaceArtifactFocus | undefined {
  const key = requestKey(path);
  const entry = pendingFocus.get(key);
  if (!entry) return undefined;
  if (Date.now() - entry.at > REQUEST_TTL_MS) {
    pendingFocus.delete(key);
    return undefined;
  }
  return entry.focus;
}

/**
 * Drop the request for `path` once a viewer has shown it. With `focus`, only
 * that request: a newer one made meanwhile stays for the viewer to show.
 */
export function clearWorkspaceArtifactFocusRequest(
  path: string,
  focus?: WorkspaceArtifactFocus
): void {
  const key = requestKey(path);
  if (focus && pendingFocus.get(key)?.focus !== focus) return;
  pendingFocus.delete(key);
}

/** Claim (and clear) the focus requested for `path`, if any. */
export function takeWorkspaceArtifactFocusRequest(
  path: string
): WorkspaceArtifactFocus | undefined {
  const focus = peekWorkspaceArtifactFocusRequest(path);
  pendingFocus.delete(requestKey(path));
  return focus;
}

/** Hear focus requests while a viewer is mounted. Returns the unsubscribe. */
export function subscribeWorkspaceArtifactFocusRequests(
  listener: FocusRequestListener
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
